/**
 * SectionRenderer - dispatches to the correct content component based on SectionKind
 * @module screen-view-v2/components/screens/FlexibleLayout
 */

import React, { memo, useCallback, useContext } from 'react';
import { CustomPurchaseSlotContext } from '@modules/screen-view-v2/customPurchaseSlot';
import type { CustomPurchaseSlotInput } from '@modules/screen-view-v2/customPurchaseSlot';
import { WebEmbed } from '@modules/screen-view-v2/components/screens/WebEmbed/WebEmbed';
import { PurchaseSlotContext } from '@modules/screen-view-v2/purchaseSlot';
import { isPurchaseButton } from '@modules/screen-view-v2/components/screens/FlexibleLayout/linkedProductFallbacks';
import type { LayoutSection, OptionsItem, ProductOffer, StepNode } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import type { SectionSemanticTags } from '@modules/screen-view-v2/utils/semanticMarkup';
import { ArticleRenderContext } from '@modules/screen-view-v2/context/ArticleRenderContext';
import { ArticleFigure } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleFigure';
import { ArticleList } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleList';
import { ArticleBreadcrumb } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleBreadcrumb';
import { ArticleShortAnswer } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleShortAnswer';
import { ArticleTakeaways } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleTakeaways';
import { ArticleCta } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleCta';
import { ArticleFooter } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleFooter';
import { ArticleByline } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleByline';
import { ArticleToc } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleToc';
import { ArticleRelated } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleRelated';
import { ArticleIndexPreviewContext } from '@modules/screen-view-v2/context/ArticleIndexPreviewContext';
import { ArticleIndex } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/ArticleIndex';
import { resolveLocaleText } from '@modules/screen-view-v2/utils/articleText';

import { CopyBlock } from '../../shared/CopyBlock';
import { Media } from '../../shared/Media';
import { ProductCatalog } from '../../shared/ProductCatalog';
import { Button } from '../../shared/Button';
import { OptionsList } from '../../shared/OptionsList';
import { PaywallHeader } from '../../shared/PaywallHeader';
import { HeaderBar } from '../../shared/HeaderBar';
import { ActionBar } from '../../shared/ActionBar';
import { Field } from '../../shared/Field';
import { IframeRenderer } from '../WebEmbed/IframeRenderer';
import type {
  CustomScreenButtonActionRequest,
  CustomScreenOptionActionRequest,
  LegacyInitData,
} from '../../../types/customScreenBridge.types';

export interface SectionRendererProps {
  section: LayoutSection;
  purchaseProductId?: string;
  step: StepNode;
  ssrMode?: boolean;
  locale: string;
  variables?: TemplateVariables;
  /** Pre-resolved screen link values */
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  /** Callback for all screen events */
  onEvent?: OnEventCallback;
  previewMode?: PreviewMode;
  editPath?: string;
  /** For ProductCatalog: currently selected product index */
  selectedProductIndex?: number | null;
  /** For ProductCatalog: callback when product is selected */
  onProductSelect?: (item: ProductOffer, index: number) => void;
  /** For ProductCatalog: per-product template variables */
  itemVariablesMap?: Record<string, TemplateVariables>;
  /** For OptionsList: set of selected item indices */
  selectedOptionIndices?: Set<number>;
  /** For OptionsList: callback when option is selected */
  onOptionSelect?: (item: OptionsItem, index: number, selected: boolean) => void;
  /** For CustomEmbed: raw HTML content (keyed by section.id) */
  embedHtmlMap?: Record<string, string>;
  /** For CustomEmbed: init data (keyed by section.id) */
  embedInitDataMap?: Record<string, LegacyInitData>;
  /** For CustomEmbed: callback when iframe requests forward navigation (already scoped to sectionId) */
  onEmbedNavigate?: (data: Record<string, unknown>) => void;
  /** For CustomEmbed: callback when iframe requests back navigation */
  onEmbedBack?: () => void;
  /** For CustomEmbed: callback for analytics events */
  onEmbedTrackEvent?: (name: string, params?: Record<string, unknown>) => void;
  /** For CustomEmbed: callback when sdk.setValue() is called */
  onEmbedValueChange?: (key: string, value: unknown) => void;
  /** For CustomEmbed: callback when sdk.setVariable() is called */
  onEmbedSetVariable?: (variableId: string, value: unknown) => void;
  /** For CustomEmbed: callback when sdk.triggerOptionAction() is called */
  onEmbedTriggerOptionAction?: (request: CustomScreenOptionActionRequest) => void;
  /** For CustomEmbed: callback when sdk.triggerButtonAction() is called */
  onEmbedTriggerButtonAction?: (request: CustomScreenButtonActionRequest) => void;
  /** Variable update broadcast for cross-section reactivity */
  lastVariableUpdate?: { variableId: string; value: unknown; sourceIframeId?: string; _ts: number } | null;
  /** For CustomEmbed: callback when sdk.selectProduct() is called */
  onEmbedSelectProduct?: (childSectionId: string, productIndex: number, productId: string) => void;
  /** For CustomEmbed: callback when sdk.purchaseProduct() is called */
  onEmbedPurchaseProduct?: (requestId: string, productId: string, childSectionId?: string) => void;
  /** Purchase result to push back to CustomEmbed iframe */
  embedPurchaseResult?: { requestId: string; status: 'success' | 'cancel' | 'error'; error?: string; _ts: number } | null;
  /** For Button sections: whether the button should be disabled (from parent section visibility) */
  buttonDisabled?: boolean;
  /** Pending purchase: reuse the shared button's spinner and busy/disabled semantics. */
  isLoading?: boolean;
  /** For TextField sections: current text value */
  textFieldValue?: string;
  /** For TextField sections: current validation error */
  textFieldError?: string | null;
  /** For TextField sections: callback when value changes */
  onTextFieldChange?: (value: string) => void;
  /** For TextField sections: callback on blur (validation) */
  onTextFieldBlur?: () => void;
  /** Article outline for this section (Text only); absent outside articles. */
  semanticTags?: SectionSemanticTags;
}

export const SectionRenderer = memo(function SectionRenderer({
  section,
  purchaseProductId,
  step,
  ssrMode,
  locale,
  variables,
  resolvedScreenValues: _resolvedScreenValues,
  onAction,
  onEvent: _onEvent,
  previewMode,
  editPath,
  selectedProductIndex,
  onProductSelect,
  itemVariablesMap,
  selectedOptionIndices,
  onOptionSelect,
  embedHtmlMap,
  embedInitDataMap,
  onEmbedNavigate,
  onEmbedBack,
  onEmbedTrackEvent,
  onEmbedValueChange,
  onEmbedSetVariable,
  onEmbedTriggerOptionAction,
  onEmbedTriggerButtonAction,
  lastVariableUpdate,
  onEmbedSelectProduct,
  onEmbedPurchaseProduct,
  embedPurchaseResult,
  buttonDisabled,
  isLoading = false,
  textFieldValue,
  textFieldError,
  onTextFieldChange,
  onTextFieldBlur,
  semanticTags,
}: SectionRendererProps): React.ReactElement | null {
  const renderSlot = useContext(PurchaseSlotContext);
  const renderCustomSlot = useContext(CustomPurchaseSlotContext);
  // Article template (G2): null outside article documents → every case below keeps today's output.
  const article = useContext(ArticleRenderContext);
  // Editor preview only (C-24): a provider exists solely in the Studio editor; published pages and funnels get null.
  const indexPreview = useContext(ArticleIndexPreviewContext);
  // Keep the embed's message/DOM namespace at section.id, while payment ownership
  // remains with the containing screen. The SDK cannot override either binding.
  const renderScopedCustomSlot = useCallback((input: CustomPurchaseSlotInput) =>
    renderCustomSlot!({ ...input, screenId: step.id, embedSectionId: section.id }),
  [renderCustomSlot, step.id, section.id]);
  switch (section.kind) {
    case 'Text':
      return (
        <CopyBlock
          copyBlock={section.textContent}
          locale={locale}
          variables={variables}
          editPath={editPath}
          titleAs={semanticTags?.title}
          subtitleAs={semanticTags?.subtitle}
          titleId={semanticTags?.titleId}
          subtitleId={semanticTags?.subtitleId}
        />
      );

    case 'Media':
      // Article template (G2, D6; Q3 option B): a template article (presentation with token maps) gets
      // figure + img[alt] + figcaption and the native article video; every other article document keeps
      // today's Media with only the resolved alt text; funnels pass no alt (today's alt="").
      if (article?.presentation) {
        return <ArticleFigure media={section.mediaContent} locale={locale} editPath={editPath} previewMode={previewMode} lead={article.leadFigureSectionId === section.id} />;
      }
      return (
        <Media
          media={section.mediaContent}
          locale={locale}
          editPath={editPath}
          fillContainer
          previewMode={previewMode}
          alt={article ? resolveLocaleText(section.mediaContent?.alt, locale) : undefined}
        />
      );

    case 'ProductCatalog':
      return (
        <ProductCatalog
          catalog={section.productCatalogContent}
          locale={locale}
          variables={variables}
          itemVariablesMap={itemVariablesMap}
          selectedIndex={selectedProductIndex}
          onSelect={onProductSelect}
          editPath={editPath}
          previewMode={previewMode}
        />
      );

    case 'Button': {
      const fallback = (
        <Button
          tapTarget={section.buttonContent}
          locale={locale}
          variables={variables}
          step={step}
          onAction={onAction}
          disabled={buttonDisabled}
          isLoading={isLoading && isPurchaseButton(section)}
          editPath={editPath}
        />
      );
      return renderSlot && isPurchaseButton(section) && section.buttonContent
        ? renderSlot({ action: section.buttonContent, step, source: 'flexibleLayout', sectionId: section.id,
          productId: purchaseProductId, disabled: buttonDisabled, fallback }) : fallback;
    }

    case 'OptionsList':
    case 'SingleSelectionList':
    case 'MultipleSelectionList':
    case 'BulletList':
      // Article template (G2, D7; Q3 option B): a BulletList in a TEMPLATE article is a real ul > li;
      // a legacy article (no presentation) keeps today's OptionsList.
      if (article?.presentation && section.kind === 'BulletList') {
        return (
          <ArticleList
            options={section.optionsListContent}
            locale={locale}
            variables={variables}
            editPath={editPath}
          />
        );
      }
      return (
        <OptionsList
          options={section.optionsListContent}
          locale={locale}
          variables={variables}
          selectedIndices={selectedOptionIndices}
          onSelect={onOptionSelect}
          editPath={editPath}
          previewMode={previewMode}
          step={step}
        />
      );

    case 'PaywallHeader': {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const phContent = (section as any).paywallHeaderContent;
      return (
        <PaywallHeader
          closeButton={phContent?.closeButton}
          restoreLabel={phContent?.restoreLabel}
          headerAppearance={phContent?.headerAppearance}
          step={step}
          locale={locale}
          onAction={onAction}
          editPath={editPath ? `${editPath}.paywallHeaderContent` : undefined}
        />
      );
    }

    case 'CustomEmbed': {
      const htmlContent = embedHtmlMap?.[section.id];
      const initData = embedInitDataMap?.[section.id];
      if (!htmlContent && !initData) return null;
      const needsAutoResize = !section.layout?.heightKind || section.layout.heightKind === 'auto';
      const renderEmbed = section.embedContent?.isIframe === false
        ? <WebEmbed
          key={`${step.id}:${section.id}`}
          embedded
          ssrMode={ssrMode}
          step={{ ...step, id: section.id, screenType: 'WebEmbed', content: { ...step.content, embed: section.embedContent } }}
          locale={locale}
          variables={variables}
          previewMode={previewMode}
          htmlContent={htmlContent ?? ''}
          initData={initData}
          onNavigate={onEmbedNavigate}
          onBack={onEmbedBack}
          onTrackEvent={onEmbedTrackEvent}
          onValueChange={onEmbedValueChange}
          onSetVariable={onEmbedSetVariable}
          onTriggerOptionAction={onEmbedTriggerOptionAction}
          onTriggerButtonAction={onEmbedTriggerButtonAction}
          onSelectProduct={onEmbedSelectProduct}
          onPurchaseProduct={onEmbedPurchaseProduct}
          lastVariableUpdate={lastVariableUpdate}
          lastPurchaseResult={embedPurchaseResult}
        />
        : <IframeRenderer
          key={`${step.id}:${section.id}`}
          htmlContent={htmlContent ?? ''}
          screenId={section.id}
          initData={initData ?? {
            inputs: {},
            labels: {},
            userData: {},
            customData: {},
            webOnboarding: { onboardingId: '' },
            incomeUserData: { ip: null, userAgent: '' },
            language: 'en',
            screenId: section.id,
          } as LegacyInitData}
          onNavigate={onEmbedNavigate}
          onBack={onEmbedBack}
          onTrackEvent={onEmbedTrackEvent}
          onValueChange={onEmbedValueChange}
          onSetVariable={onEmbedSetVariable}
          onTriggerOptionAction={onEmbedTriggerOptionAction}
          onTriggerButtonAction={onEmbedTriggerButtonAction}
          onSelectProduct={onEmbedSelectProduct}
          onPurchaseProduct={onEmbedPurchaseProduct}
          lastVariableUpdate={lastVariableUpdate}
          lastPurchaseResult={embedPurchaseResult}
          style={{ width: '100%', minHeight: 300, border: 'none' }}
          autoResize={needsAutoResize}
        />;
      return <CustomPurchaseSlotContext.Provider value={renderCustomSlot ? renderScopedCustomSlot : null}>
        {renderEmbed}
      </CustomPurchaseSlotContext.Provider>;
    }

    case 'TextField':
      return (
        <Field
          textField={section.textFieldContent}
          locale={locale}
          value={textFieldValue ?? ''}
          error={textFieldError}
          onChange={onTextFieldChange}
          onBlur={onTextFieldBlur}
          editPath={editPath}
          previewMode={previewMode}
        />
      );

    // Header section — render inside the scroll container at its order position
    case 'Header': {
      const { content } = step;
      return content?.header ? (
        <HeaderBar
          header={content.header}
          locale={locale}
          variables={variables}
          step={step}
          onAction={onAction}
          editPath={editPath ? 'content.header' : undefined}
        />
      ) : null;
    }

    // Footer section — render inline as ActionBar at its order position
    case 'Footer': {
      const { content: footerContent } = step;
      return footerContent?.actionBar ? (
        <ActionBar
          actionBar={footerContent.actionBar}
          locale={locale}
          variables={variables}
          step={step}
          onAction={onAction}
          editPath={editPath ? 'content.actionBar' : undefined}
        />
      ) : null;
    }

    // Article template (G2, D9): article block kinds render only inside the article context;
    // outside it they stay `null`, exactly like today's `default` branch.
    case 'ArticleBreadcrumb':
      return article ? <ArticleBreadcrumb section={section} locale={locale} article={article} /> : null;
    case 'ArticleShortAnswer':
      return article ? <ArticleShortAnswer section={section} locale={locale} article={article} /> : null;
    case 'ArticleTakeaways':
      return article ? <ArticleTakeaways section={section} locale={locale} article={article} /> : null;
    case 'ArticleCta':
      return article ? <ArticleCta section={section} locale={locale} article={article} /> : null;
    case 'ArticleFooter':
      return article ? <ArticleFooter section={section} locale={locale} article={article} /> : null;
    case 'ArticleByline':
      return article ? <ArticleByline section={section} locale={locale} article={article} /> : null;
    case 'ArticleToc':
      return article ? <ArticleToc section={section} locale={locale} article={article} /> : null;
    case 'ArticleRelated':
      return article ? <ArticleRelated section={section} locale={locale} article={article} /> : null;
    case 'ArticleIndex':
      return article ?? indexPreview
        ? <ArticleIndex section={section} locale={locale} article={article ?? indexPreview!.article} />
        : null;

    // EmptySpace — layout-only spacer section, no content to render
    case 'EmptySpace':
    // Screen-level section — rendered by FlexibleLayout container, not as section content
    case 'Background':
      return null;

    default:
      return null;
  }
});

SectionRenderer.displayName = 'SectionRenderer';
